// Client-side photo edits (rotate / straighten / crop), applied before
// upload so Cloudinary only ever stores the corrected pixels. No server or
// schema involvement: the edited JPEG is what gets uploaded, and Cloudinary
// reports its dimensions back as usual (see lib/uploads.ts).
//
// Coordinate model: edits are expressed in the "display frame" — the image
// after EXIF orientation, then coarse 90° rotation, then straightening.
// - `rotate` swaps the frame's width/height for 90/270.
// - `straighten` spins the image inside the frame about its center, scaled
//   up just enough (coverScale) that no blank corners appear.
// - `crop` is a rect in normalized [0,1] frame coordinates.
// Edits are kept alongside the ORIGINAL file and re-applied from it each
// time, so re-editing never compounds JPEG re-encoding losses.

export type CoarseRotation = 0 | 90 | 180 | 270

export interface CropRect {
	x: number
	y: number
	width: number
	height: number
}

export interface PhotoEdits {
	rotate: CoarseRotation
	/** Fine straightening angle in degrees, clockwise-positive. */
	straighten: number
	/** Normalized crop in display-frame coords, or null for the full frame. */
	crop: CropRect | null
}

export const STRAIGHTEN_LIMIT_DEG = 15

export function defaultEdits(): PhotoEdits {
	return { rotate: 0, straighten: 0, crop: null }
}

export function isNoopEdits(edits: PhotoEdits): boolean {
	return edits.rotate === 0 && edits.straighten === 0 && edits.crop === null
}

/** Display-frame size for a bitmap under a coarse rotation. */
export function frameSize(
	bitmapWidth: number,
	bitmapHeight: number,
	rotate: CoarseRotation,
): { width: number; height: number } {
	return rotate === 90 || rotate === 270
		? { width: bitmapHeight, height: bitmapWidth }
		: { width: bitmapWidth, height: bitmapHeight }
}

/**
 * Scale factor that keeps a w×h image covering its own w×h frame while
 * rotated by `deg`. Derived from the frame's corner (w/2, h/2) staying
 * inside the rotated image: s = cosθ + sinθ·max(w/h, h/w).
 */
export function coverScale(width: number, height: number, deg: number): number {
	const theta = (Math.abs(deg) * Math.PI) / 180
	const aspect = Math.max(width / height, height / width)
	return Math.cos(theta) + Math.sin(theta) * aspect
}

/** Rotates a normalized crop rect 90° clockwise within the frame, so an
 *  existing crop stays on the same image content when the frame rotates. */
export function rotateCropCW(crop: CropRect): CropRect {
	return {
		x: 1 - (crop.y + crop.height),
		y: crop.x,
		width: crop.height,
		height: crop.width,
	}
}

/** Same, counter-clockwise. */
export function rotateCropCCW(crop: CropRect): CropRect {
	return {
		x: crop.y,
		y: 1 - (crop.x + crop.width),
		width: crop.height,
		height: crop.width,
	}
}

/**
 * Sets `ctx` up so drawing the bitmap centered at the origin lands it in
 * the display frame with rotation + straighten(+cover scale) applied.
 * Callers are expected to have already mapped the context into frame
 * coordinates (origin at the frame's top-left, units = frame pixels).
 * Shared by the editor's live preview and the final render so the two can
 * never disagree.
 */
export function applyFrameTransform(
	ctx: CanvasRenderingContext2D,
	bitmapWidth: number,
	bitmapHeight: number,
	edits: PhotoEdits,
): void {
	const frame = frameSize(bitmapWidth, bitmapHeight, edits.rotate)
	const scale = coverScale(frame.width, frame.height, edits.straighten)
	ctx.translate(frame.width / 2, frame.height / 2)
	ctx.rotate((edits.straighten * Math.PI) / 180)
	ctx.scale(scale, scale)
	ctx.rotate((edits.rotate * Math.PI) / 180)
}

async function decodeBitmap(source: File | Blob): Promise<ImageBitmap> {
	// Explicit "from-image" so EXIF orientation is honored everywhere —
	// otherwise phone photos that rely on the EXIF flag would come out
	// double-rotated after our own rotation is baked in.
	return createImageBitmap(source, { imageOrientation: "from-image" })
}

export interface RenderedImage {
	blob: Blob
	width: number
	height: number
}

/**
 * Applies `edits` to the original file and returns a JPEG blob. `maxDim`
 * caps the longest output side (used for cheap thumbnail previews); omit
 * it for the full-resolution upload render.
 */
export async function renderEditedImage(
	source: File | Blob,
	edits: PhotoEdits,
	maxDim?: number,
): Promise<RenderedImage> {
	const bitmap = await decodeBitmap(source)
	try {
		const frame = frameSize(bitmap.width, bitmap.height, edits.rotate)
		const crop = edits.crop ?? { x: 0, y: 0, width: 1, height: 1 }
		const cropW = Math.max(1, Math.round(crop.width * frame.width))
		const cropH = Math.max(1, Math.round(crop.height * frame.height))
		const outScale = maxDim ? Math.min(1, maxDim / Math.max(cropW, cropH)) : 1
		const outW = Math.max(1, Math.round(cropW * outScale))
		const outH = Math.max(1, Math.round(cropH * outScale))

		const canvas = document.createElement("canvas")
		canvas.width = outW
		canvas.height = outH
		const ctx = canvas.getContext("2d")
		if (!ctx) throw new Error("Canvas 2D is not supported in this browser")

		ctx.imageSmoothingQuality = "high"
		ctx.scale(outScale, outScale)
		ctx.translate(-crop.x * frame.width, -crop.y * frame.height)
		applyFrameTransform(ctx, bitmap.width, bitmap.height, edits)
		ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2)

		const blob = await new Promise<Blob>((resolve, reject) => {
			canvas.toBlob(
				(b) => (b ? resolve(b) : reject(new Error("Failed to encode image"))),
				"image/jpeg",
				0.92,
			)
		})
		return { blob, width: outW, height: outH }
	} finally {
		bitmap.close()
	}
}

export { decodeBitmap }
